import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import {
  governanceApi,
  type BrandVoice,
  type CorrectableTraits,
  type VoiceReview
} from '../../services/api'
import {
  DERIVATION_WINDOW_MS,
  EMOJI_LABELS,
  FORMALITY_LABELS,
  LENGTH_LABELS,
  POLL_MS,
  TONE_LABELS,
  TONE_ORDER,
  TRAIT_LABELS,
  TRAIT_ORDER,
  effectiveTraits,
  formatDay,
  refusalMessage,
  traitValueLabel,
  usableDerivation,
  type DisplayTraits
} from './voiceReview'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardList,
  Chip,
  ChipGroup,
  ErrorState,
  Eyebrow,
  FieldLabel,
  Hint,
  LoadingState,
  Orb,
} from '../../ds'

/**
 * How I think you sound — the derived voice, shown back for confirmation (v5 app IA §3.6, §7).
 *
 * ── What this card is ───────────────────────────────────────────────────────────────────
 *
 * A REVIEW, not a form. `MAR-106` shipped a complete deriver that no client called: the voice
 * was read from the storefront, written to the row as `proposed`, and refused by `getBrandVoice`
 * forever, because nothing existed that could say yes. This is the yes.
 *
 * Everything on screen is rendered from the server's `state`. The card never infers one — the
 * six-minute in-flight window is the server's to know (`DERIVATION_IN_FLIGHT_MS`), and a card
 * computing its own would offer a button the API would refuse.
 *
 * ── What it deliberately does not do ────────────────────────────────────────────────────
 *
 * **There is no auto-derive.** Not at connect, not lazily on first view. It would spend a
 * storefront fetch and a Bedrock call because a merchant opened a tab — work they did not ask
 * for, on a surface whose whole argument is that the merchant reads and answers (§7.4). The
 * idle card asks instead, and that button is also the backfill for every existing store.
 *
 * ── The two things a confirmation actually moves ────────────────────────────────────────
 *
 * 1. **It supersedes the merchant's own markers** for the four traits on the card. Clicking
 *    "That sounds like us" about a card showing `tone: friendly` IS the merchant stating
 *    friendly. So the card names which of their settings this will replace — the opposite of
 *    what an earlier draft of §7.5 said, and `voiceReviewPayload` carries a comment warning
 *    that the card author would otherwise have built the screen from that sentence.
 * 2. **It moves words shoppers read.** `toneForBrandVoice` collapses tone to `warm`/`direct`
 *    to select LOCKED storefront-widget copy (`packages/shared/src/suitability-packs.js`),
 *    so tone is not only an instruction to the model. The card has to say so.
 *
 * ── Correcting IS confirming ────────────────────────────────────────────────────────────
 *
 * They could not be two acts: a correction written before a confirm was removed by that
 * confirm, and the derivation won at read time. So "Save these" posts to `…/voice/correct`,
 * which writes the corrected traits as merchant-stated and confirms in the same write.
 *
 * ⚠️ And it sends ONLY the traits whose value changed. Sending all four would stamp a merchant
 * marker on traits the merchant never touched and freeze them against every future derivation.
 *
 * ── The guards, all three borrowed from `AuthorityPanel` ────────────────────────────────
 *
 * The snapshot carries its own `workspaceId` and is matched at the READ, so the render that
 * happens before any effect runs cannot paint workspace A's proposal under workspace B's name
 * (PR #103, three review rounds). Request generations are kept per workspace so a late response
 * cannot land in the slot. And every post-await step is gated on the workspace still being the
 * one on screen.
 */



/** Everything derived from a server response, stamped with the workspace it describes. */
interface Snapshot {
  workspaceId: string
  /** `null` with `failed: true` means "we could not find out", never "nothing derived". */
  review: VoiceReview | null
  failed: boolean
  /**
   * The voice every consumer is USING right now (the gated read). Held beside the proposal
   * because "which of your settings will this replace" is a question about both, and the
   * review route deliberately carries only the derived half.
   */
  live: BrandVoice | null
  liveFailed: boolean
}

interface Feedback {
  workspaceId: string
  error?: string
  note?: string
}

interface Correction {
  workspaceId: string
  /** The proposal being corrected. A correction confirms, so it carries the same pin. */
  derivedAt: string
  traits: CorrectableTraits
}

interface DerivedVoiceCardProps {
  /**
   * A confirmation or a correction landed. The form below this card seeds itself in a mount
   * effect, so without a remount it keeps showing a voice this card has just superseded.
   */
  onVoiceChanged?: () => void
  /**
   * The other direction, and the card's whole receipt claim depends on it.
   *
   * A save in the voice form below stamps a merchant marker and MOVES the effective voice —
   * so "This is what she uses now" above goes false in the same tab, and stays false until a
   * reload. The parent bumps this counter on a successful save and the card re-reads its own
   * pair; it is a token rather than the data because the card, not the tab, owns that read —
   * a second owner of this state is how `AuthorityPanel` took three review rounds.
   *
   * ⚠️ A COUNTER, not a boolean and not a timestamp: two saves in one session must re-read
   * twice, and a counter cannot collide with itself.
   */
  reloadToken?: number
  /**
   * Every review this card reads, as it lands (MAR-384). The Brand tab's section hints and
   * overview keep a read of their own; reporting here keeps them in step with what this card
   * shows — a derive started here flips the nav to "Reading…" without a second poll.
   */
  onRead?: (read: { workspaceId: string; review: VoiceReview; live: BrandVoice | null }) => void
  /**
   * A derive started elsewhere on the tab is still in flight (MAR-384). ⚠️ The overview's "Read
   * it again" and this card's are two buttons for one server run: opening How we sound before
   * the overview's 202 must not mount a card that offers a second derive.
   */
  deriving?: boolean
  /** This card started a derive; `done` settles when it has. The other half of `deriving`. */
  onDerive?: (workspaceId: string, done: Promise<unknown>) => void
}

export default function DerivedVoiceCard({ onVoiceChanged, reloadToken = 0, onRead, deriving = false, onDerive }: DerivedVoiceCardProps) {
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const workspaceId = currentWorkspace?.id

  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [correction, setCorrection] = useState<Correction | null>(null)
  /**
   * The poll ran out the server's window and the row still says `deriving`.
   *
   * ⚠️ Carries the RUN it gave up on, not just the workspace — scoped at the read, like every
   * other cached value in this file. A derivation started in another tab after this card gave
   * up puts a NEW `requestedAt` on the row, and a flag keyed on the workspace alone would have
   * the card saying "over six minutes" about a run that started seconds ago.
   */
  const [exhausted, setExhausted] = useState<{ workspaceId: string; requestedAt: string } | null>(null)

  // A ref, so a parent passing a fresh callback each render never re-creates `load` (and with
  // it the read effect).
  const onReadRef = useRef(onRead)
  useEffect(() => { onReadRef.current = onRead }, [onRead])

  /** Request generation PER WORKSPACE — only the newest request for a workspace may write. */
  const requestSeq = useRef<Record<string, number>>({})

  /** Is `asked` still the workspace on screen? Read from the store at CALL time. */
  const stillCurrent = useCallback(
    (asked: string) => useWorkspaceStore.getState().currentWorkspace?.id === asked,
    []
  )

  // ── Scoped at the read. A value belonging to another workspace is simply not current. ──
  const current = snapshot?.workspaceId === workspaceId ? snapshot : null
  const review = current?.review ?? null
  // ⚠️ Narrowed at the READ, like everything else here. `review.derived` is typed as a
  // `DerivedVoice` and arrives raw, so the type is a claim about the route's intent, not about
  // this row. Anything the card cannot render is `null` from here down, and the one branch
  // that handles `null` offers the re-read.
  const derived = usableDerivation(review?.derived)
  const state = review?.state
  const shown = feedback && feedback.workspaceId === workspaceId ? feedback : null
  const editing = correction && correction.workspaceId === workspaceId ? correction : null
  const working = busy === workspaceId || deriving

  const apply = useCallback((asked: string, seq: number, snap: Snapshot) => {
    if (requestSeq.current[asked] !== seq) return
    if (!stillCurrent(asked)) return
    setSnapshot(snap)
    if (snap.review) onReadRef.current?.({ workspaceId: asked, review: snap.review, live: snap.live })
  }, [stillCurrent])

  /**
   * Read the review and the live voice together.
   *
   * ⚠️ Two reads, one snapshot. `GET …/governance/voice` answers what was DERIVED; the
   * merchant's current voice is the gated `GET …/brand-voice`. The card needs both to say
   * which of the merchant's own settings a confirmation will replace, and a failure of the
   * second must not blank the first — it only silences that one sentence.
   */
  const load = useCallback(async(asked: string): Promise<VoiceReview | null> => {
    const seq = (requestSeq.current[asked] = (requestSeq.current[asked] ?? 0) + 1)
    try {
      const [res, liveRes] = await Promise.all([
        governanceApi.getVoiceReview(asked),
        // ⚠️ Never allowed to reject the pair. The review is the card; the live voice only
        // decides whether ONE sentence can be said, so its failure must not blank the screen.
        governanceApi.getBrandVoice(asked).catch(() => ({ success: false, data: undefined }))
      ])
      const live = liveRes.success ? liveRes.data ?? null : null
      if (res.success && res.data) {
        apply(asked, seq, { workspaceId: asked, review: res.data, failed: false, live, liveFailed: !live })
        return res.data
      }
      apply(asked, seq, { workspaceId: asked, review: null, failed: true, live, liveFailed: !live })
      return null
    } catch {
      apply(asked, seq, { workspaceId: asked, review: null, failed: true, live: null, liveFailed: true })
      return null
    }
  }, [apply])

  // The first read, and every re-read the tab asks for. ⚠️ `reloadToken` belongs in THIS
  // effect rather than in one of its own: a save below moves the same pair the mount read
  // fetches, and two effects owning one read is two request generations to keep in step.
  useEffect(() => {
    if (!workspaceId) return
    void load(workspaceId)
  }, [load, workspaceId, reloadToken])

  /**
   * While she is reading, ask again every five seconds — and give up at the server's window.
   *
   * ⚠️ Bounded on purpose. Nothing clears the in-flight marker: a run that produced nothing
   * (the deriver returns its failures rather than throwing) leaves no trace at all, and the
   * ONLY way out of `deriving` is the marker ageing past `DERIVATION_IN_FLIGHT_MS`. A poll
   * with no ceiling would sit on a dead run forever, one request every five seconds.
   *
   * The interval is torn down by the effect's own cleanup on a state change, a workspace
   * change and an unmount, so none of those needs its own guard.
   */
  const requestedAt = review?.derivationRequest?.requestedAt
  // Scoped at the read: a giving-up recorded for another workspace, or for an earlier run, is
  // simply not about what is on screen.
  const gaveUp = !!exhausted && exhausted.workspaceId === workspaceId && exhausted.requestedAt === requestedAt
  useEffect(() => {
    if (!workspaceId || state !== 'deriving') return
    const requestedMs = Date.parse(requestedAt || '')
    const timer = setInterval(() => {
      if (Number.isFinite(requestedMs) && Date.now() - requestedMs >= DERIVATION_WINDOW_MS) {
        clearInterval(timer)
        setExhausted({ workspaceId, requestedAt: requestedAt || '' })
        // One last read: the server flips out of `deriving` on its own once the marker ages,
        // so the honest ending is usually on the other side of this call.
        void load(workspaceId)
        return
      }
      void load(workspaceId)
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [workspaceId, state, requestedAt, load])

  /** One place every act goes through, so the refusal handling cannot drift between them. */
  const run = useCallback(async(
    asked: string,
    call: () => Promise<{ success: boolean; data?: VoiceReview; error?: string; details?: unknown }>,
    note: string | null,
    changed: boolean
  ) => {
    setBusy(asked)
    setFeedback(null)
    try {
      const res = await call()
      if (!stillCurrent(asked)) return
      if (!res.success) {
        // `details` is `unknown` on purpose — a 400 can carry Zod's error ARRAY, not an object.
        const reason = (res.details as { reason?: unknown } | undefined)?.reason
        setFeedback({ workspaceId: asked, error: refusalMessage(reason, res.error || 'That did not go through.') })
        // ⚠️ Re-read on every refusal, not only on a 409. A superseded proposal must be
        // REPLACED on screen by the newer one — leaving the answered-and-refused block up is
        // an invitation to answer it again, which is the same rubber stamp one step out.
        await load(asked)
        // ⚠️ `already_confirmed` is the one refusal that KEEPS the pending edits. Every other
        // one replaces the proposal they were about, so they are edits to something that no
        // longer exists. This one refuses a correction to the proposal still on the row —
        // the values the merchant typed are the only copy of them left, and dropping them
        // loses typed input to a race. The confirmed card reads them back below.
        if (stillCurrent(asked) && reason !== 'already_confirmed') setCorrection(null)
        return
      }
      // ⚠️ The write's own payload is DISCARDED and the pair is re-read instead. Every act
      // here moves the live voice as well as the proposal — a correction writes merchant
      // traits, a confirmation supersedes markers — and the write answers with only half of
      // that. Applying it would leave the card naming a supersede list computed against a
      // voice two states old, which is the stale-half failure one level out.
      await load(asked)
      if (!stillCurrent(asked)) return
      setCorrection(null)
      if (note) setFeedback({ workspaceId: asked, note })
      if (changed) onVoiceChanged?.()
    } catch (e) {
      if (!stillCurrent(asked)) return
      setFeedback({ workspaceId: asked, error: e instanceof Error ? e.message : 'That did not go through.' })
    } finally {
      setBusy((b) => (b === asked ? null : b))
    }
  }, [load, stillCurrent, onVoiceChanged])

  const derive = () => {
    if (!workspaceId || working) return
    // No imperative clear of `exhausted`: a new derivation writes a new `requestedAt`, and the
    // read-scope above stops matching on its own. One fewer thing to forget.
    const done = run(workspaceId, () => governanceApi.deriveVoice(workspaceId), null, false)
    onDerive?.(workspaceId, done)
  }

  /**
   * ⚠️ `derivedAt` comes from the RENDERED block, passed in at the call site, and is never
   * re-read from the freshest thing the card knows. Confirming a proposal nobody read is
   * exactly what this screen exists to prevent, and the server answers 409 when they differ.
   */
  const confirm = (renderedDerivedAt: string) => {
    if (!workspaceId) return
    void run(
      workspaceId,
      () => governanceApi.confirmVoice(workspaceId, { derivedAt: renderedDerivedAt }),
      'Confirmed. This is how she sounds from now on.',
      true
    )
  }

  /**
   * ⚠️ ONLY the traits whose value changed. Sending all four stamps a merchant marker on
   * traits the merchant never touched, freezing them against every future derivation.
   */
  const saveCorrection = () => {
    if (!workspaceId || !editing || !derived) return
    const traits: Partial<CorrectableTraits> = {}
    for (const key of TRAIT_ORDER) {
      if (editing.traits[key] !== derived.traits[key]) {
        // A per-key assignment rather than a spread, so the object only ever grows a key that
        // actually differs.
        Object.assign(traits, { [key]: editing.traits[key] })
      }
    }
    if (Object.keys(traits).length === 0) return
    void run(
      workspaceId,
      () => governanceApi.correctVoice(workspaceId, { derivedAt: editing.derivedAt, traits }),
      'Corrected and confirmed. This is how she sounds from now on.',
      true
    )
  }

  /**
   * Which of the merchant's own settings a confirmation will REPLACE.
   *
   * A trait qualifies on two counts together: they stated it (`fieldSources` carries a marker),
   * and the derived value differs from what is live. Confirming removes the marker either way,
   * but only a differing value changes what she actually does — and that is the claim being
   * made on screen.
   */
  const superseded = useMemo(() => {
    if (!derived || !current?.live) return []
    const sources = review?.fieldSources ?? {}
    return TRAIT_ORDER.filter((key) => {
      const marker = sources[key]
      const stated = marker?.setBy === 'merchant' || marker?.merchantOverride === true
      return stated && current.live?.[key] !== derived.traits[key]
    })
  }, [derived, review, current])

  /** They stated something, but we could not read what is live, so we cannot name the diff. */
  const supersedeUnknown = !!current?.liveFailed && TRAIT_ORDER.some((key) => {
    const marker = review?.fieldSources?.[key]
    return marker?.setBy === 'merchant' || marker?.merchantOverride === true
  })

  if (!workspaceId) return null

  // ── Honest waits and honest failures. The prototype has neither; `AuthorityPanel` does. ──
  if (!current) {
    return <LoadingState caption="Reading what she has worked out…" />
  }

  if (current.failed || !review) {
    return (
      <ErrorState title="Could not read what she has worked out about your voice.">
        Nothing is shown rather than a guess. Reload to try again.
      </ErrorState>
    )
  }

  const notes = (
    <>
      {shown?.error && <Alert tone="danger" live="assertive">{shown.error}</Alert>}
      {shown?.note && !shown.error && <Alert tone="success" live="polite">{shown.note}</Alert>}
    </>
  )

  /** The idle / deriving / unreadable card: one sentence and at most one act. */
  const plainCard = (sentence: string, action: React.ReactNode) => (
    <Card>
      <CardHeader title="How I think you sound" description={sentence} />
      <CardList>{notes}</CardList>
      {action && <CardFooter>{action}</CardFooter>}
    </Card>
  )

  // ── idle ────────────────────────────────────────────────────────────────────────────────
  if (state === 'idle') {
    const attempted = review.derivationRequest?.requestedAt
    return plainCard(
      attempted
        // The run left a marker and no result. Named rather than retried silently:
        // a second crawl and a second model call are not something to spend quietly.
        ? `She started reading your storefront on ${formatDay(attempted)} and it did not finish, so there is nothing to show you. Ask her again and she will try from the start.`
        : 'She has not read your storefront yet. Nothing below is derived, learned or guessed — ask her to read it and she will write down how you sound, in her words, for you to correct.',
      <Button size="sm" busy={working} busyLabel="Asking her…" onClick={derive}>
        Read my storefront
      </Button>
    )
  }

  // ── deriving ────────────────────────────────────────────────────────────────────────────
  if (state === 'deriving') {
    return plainCard(
      gaveUp
        ? 'She has been reading your storefront for over six minutes, which is longer than it takes. Nothing has been written, and nothing was changed — ask her again when you are ready.'
        : 'Reading your storefront. This takes a couple of minutes, and you can leave this page — it carries on without you.',
      gaveUp && (
        <Button variant="ghost" size="sm" busy={working} busyLabel="Asking her…" onClick={derive}>
          Try again
        </Button>
      )
    )
  }

  // ── proposed · confirmed ────────────────────────────────────────────────────────────────
  if (!derived) {
    // Two ways in, one honest ending. The server said `proposed`/`confirmed` and carried no
    // block, OR it carried one this card cannot render — and the merchant can do exactly the
    // same thing about either. ⚠️ The wording is `refusalMessage('derivation_invalid')`, the
    // sentence the SERVER says when it refuses to confirm an unreadable block: one vocabulary
    // for one condition, so a merchant who meets it twice is not told two different stories.
    // The button matters more than the sentence — a card that threw here took the whole tab
    // down, and with it the only act that clears this state.
    return plainCard(
      refusalMessage('derivation_invalid', ''),
      <Button size="sm" busy={working} busyLabel="Asking her…" onClick={derive}>
        Read it again
      </Button>
    )
  }

  const confirmed = state === 'confirmed'
  const { traits, provenance } = derived

  /**
   * ⚠️ What she USES, which after a correction is not what she READ.
   *
   * `correctVoiceDerivation` deliberately never rewrites `derived.traits` or its provenance —
   * a correction overrides the proposal per field, it does not restate what the deriver
   * observed, and a receipt has to be able to say "she read this, you said that instead". The
   * corrected value lands on the TOP-LEVEL brand-voice field with a `setBy: 'merchant'` marker,
   * which is exactly what `applyConfirmedDerivedVoice` then keeps.
   *
   * So a confirmed card rendered from `derived.traits` shows the merchant the value they just
   * REJECTED, under a caption saying it is what she uses. Every consumer has the correction;
   * the only screen that exists says otherwise, and the merchant's rational reading is that
   * their correction did not save. The marker is the switch, and `current.live` (the gated
   * read, which is the effective voice) is the value.
   *
   * Falls back to the proposal when the live read failed — showing her reading is wrong by a
   * correction, showing nothing is wrong by a whole card.
   */
  const effective: DisplayTraits = effectiveTraits(derived, review.fieldSources, current.live)
  /** The traits where her reading and the merchant's word actually differ. */
  const corrected = TRAIT_ORDER.filter((key) => effective[key] !== traits[key])

  // Proposed: the proposal, or the edit in progress. Confirmed: what she actually uses.
  // Typed as the DISPLAY shape: both of the other two are valid display traits, and neither
  // widening reaches `correctVoice`, whose body is still built from `CorrectableTraits`.
  const shownTraits: DisplayTraits = editing ? editing.traits : confirmed ? effective : traits

  /**
   * Edits that a refusal kept rather than dropped — see the `already_confirmed` branch in
   * `run`. Pinned to the proposal they were about, so a later derivation cannot inherit them.
   */
  // ⚠️ The LABEL is built here, where `editing` is still narrowed to non-null, rather than at
  // the render site: reading `editing.traits` again inside the JSX is what the compiler refuses,
  // and widening it with a `!` would trade a type error for a runtime one the day this branch
  // renders without an edit in hand.
  const strandedEdits = confirmed && editing && editing.derivedAt === provenance.derivedAt
    ? TRAIT_ORDER
      .filter((key) => editing.traits[key] !== effective[key])
      .map((key) => `${TRAIT_LABELS[key].toLowerCase()} as ${traitValueLabel(key, editing.traits)}`)
    : []

  /** One correction control: a labelled group of select chips (`.ms-pill` → Chip select). */
  const pillGroup = <T extends string | number>(
    label: string,
    options: ReadonlyArray<{ value: T; label: string }>,
    selected: T,
    pick: (value: T) => void,
    hint?: React.ReactNode
  ) => (
    <div>
      <FieldLabel as="p" aria-hidden="true">{label}</FieldLabel>
      {hint && <Hint>{hint}</Hint>}
      <ChipGroup label={label}>
        {options.map((o) => (
          <Chip key={String(o.value)} kind="select" selected={selected === o.value} onToggle={() => pick(o.value)}>
            {o.label}
          </Chip>
        ))}
      </ChipGroup>
    </div>
  )

  return (
    <Card>
      <CardHeader
        icon={<Orb size={24} />}
        aside={
          <Chip tone={confirmed ? 'sage' : 'gold'}>
            {confirmed ? 'Confirmed' : 'Awaiting your confirmation'}
          </Chip>
        }
      >
        <Eyebrow as="span">How I think you sound</Eyebrow>
      </CardHeader>

      <CardList>
        <p className="t-voice">{traits.summary}</p>

        <ChipGroup label="How she sounds">
          {TRAIT_ORDER.map((key) => (
            <Chip key={key}>
              <span>{TRAIT_LABELS[key]}</span> <b>{traitValueLabel(key, shownTraits)}</b>
            </Chip>
          ))}
        </ChipGroup>

        {/* The grid cell for a custom tone can only say "Your own words"; this is the words.
            ⚠️ And the empty case is said, not hidden: `tone: 'custom'` with no prose behind it
            is what `buildBrandVoiceInstructions` renders as `balanced` — a voice neither party
            chose — so a card that showed only "Your own words" would be naming a tone nothing
            acts on. */}
        {shownTraits.tone === 'custom' && (
          <CardBody>
            {shownTraits.customToneDescription
              ? <>Your words for it: “{shownTraits.customToneDescription}”</>
              : 'Your own words for tone — but nothing is written down for them, so she falls back to a balanced voice. Describe it in the voice form below.'}
          </CardBody>
        )}

        <CardBody>
          <div>Read from your storefront — {provenance.sourceDomain}</div>
          <div>
            Derived {formatDay(provenance.derivedAt)}, from {provenance.pageCount}{' '}
            {provenance.pageCount === 1 ? 'page' : 'pages'} of your own copy
          </div>
          {/* Her reading is kept exactly as she made it, so where the merchant corrected one the
              two differ — and the grid above is the one that governs. Named rather than merged:
              provenance is earned, and a card that quietly overwrote her reading with the
              correction would be asserting she read something she did not. */}
          {confirmed && corrected.length > 0 && (
            <div>
              She read{' '}
              {corrected.map((key) => `${TRAIT_LABELS[key].toLowerCase()} as ${traitValueLabel(key, traits)}`).join(', ')}
              {' '}— you corrected that, and above is what she uses
            </div>
          )}
        </CardBody>

        {confirmed ? (
          <>
            <Alert tone="success">
              This is what she uses now — confirmed by you on {formatDay(derived.confirmedAt)}
              {corrected.length > 0 && ', with your corrections'}.
            </Alert>
            {notes}
            {strandedEdits.length > 0 && (
              <Alert
                tone="attention"
                actions={
                  <Button variant="ghost" size="sm" onClick={() => setCorrection(null)}>
                    Clear this
                  </Button>
                }
              >
                The correction you were making was not saved. You had{' '}
                <b>
                  {strandedEdits.join(', ')}
                </b>
                . Read it again and correct the new reading, or set these in the voice form below.
              </Alert>
            )}
          </>
        ) : (
          <>
            {/* ⚠️ Tone is not only an instruction to the model — AND the second effect is
                conditional, so the sentence has to be too. `toneForBrandVoice` has exactly two
                consumers, both inside `buildSuitabilityConfig`, and both return `{enabled:false}`
                before reaching it unless `workspace.suitability.enabled` is true WITH a
                `disclosureVersion`. That capture is structurally dark pending counsel (MAR-56),
                so "confirming changes words on your site" is false for every merchant today.
                Saying it in a PR whose purpose is deleting claims the platform does not keep
                would be the defect, not the warning. */}
            <Alert tone="info">
              Tone is not only how she writes. Where the short questions she asks shoppers on your
              storefront are switched on, it also picks which fixed version of that wording they
              see — so tone can move words on your site, not only in her messages.
            </Alert>

            {/* ⚠️ Hidden while a correction is open. "Confirming replaces what you set yourself
                for tone" is the wrong claim about a merchant who is at that moment correcting
                tone: a correction KEEPS that marker — `correctVoiceDerivation` supersedes only
                the traits the body did NOT carry. The sentence is about the confirm button, so
                it belongs only where the confirm button is. */}
            {!editing && superseded.length > 0 && (
              <Alert tone="attention">
                Confirming replaces what you set yourself for{' '}
                <b>{superseded.map((key) => TRAIT_LABELS[key].toLowerCase()).join(', ')}</b>. She is
                using your values for those today.
              </Alert>
            )}
            {!editing && superseded.length === 0 && supersedeUnknown && (
              <Alert tone="attention">
                You have set some of these yourself, and your current voice could not be read just
                now — so this cannot tell you which of your own values confirming will replace.
              </Alert>
            )}

            {notes}

            {editing && (
              <>
                {pillGroup(
                  'Tone',
                  TONE_ORDER.map((tone) => ({ value: tone, label: TONE_LABELS[tone] })),
                  editing.traits.tone,
                  (tone) => setCorrection({ ...editing, traits: { ...editing.traits, tone } }),
                  <>
                    A <b>custom</b> tone — one you describe in your own words — is set in the voice
                    form below, not here. She does not propose one.
                  </>
                )}
                {pillGroup(
                  'How formal',
                  [1, 2, 3, 4, 5].map((level) => ({ value: level, label: FORMALITY_LABELS[level - 1] })),
                  editing.traits.formalityLevel,
                  (formalityLevel) => setCorrection({ ...editing, traits: { ...editing.traits, formalityLevel } })
                )}
                {pillGroup(
                  'Emoji',
                  (Object.keys(EMOJI_LABELS) as BrandVoice['emojiUsage'][]).map((usage) => ({
                    value: usage,
                    label: EMOJI_LABELS[usage]
                  })),
                  editing.traits.emojiUsage,
                  (emojiUsage) => setCorrection({ ...editing, traits: { ...editing.traits, emojiUsage } })
                )}
                {pillGroup(
                  'How much she says',
                  (Object.keys(LENGTH_LABELS) as BrandVoice['responseLength'][]).map((len) => ({
                    value: len,
                    label: LENGTH_LABELS[len]
                  })),
                  editing.traits.responseLength,
                  (responseLength) => setCorrection({ ...editing, traits: { ...editing.traits, responseLength } })
                )}

                <Alert tone="info">
                  Saving a correction confirms it too — she starts using these straight away, and
                  only what you changed is recorded as yours.
                </Alert>
              </>
            )}
          </>
        )}
      </CardList>

      <CardFooter>
        {confirmed ? (
          <Button variant="ghost" size="sm" busy={working} busyLabel="Asking her…" onClick={derive}>
            Read it again
          </Button>
        ) : editing ? (
          <>
            <Button
              size="sm"
              busy={working}
              busyLabel="Saving…"
              disabled={TRAIT_ORDER.every((k) => editing.traits[k] === traits[k])}
              onClick={saveCorrection}
            >
              Save these
            </Button>
            <Button variant="ghost" size="sm" disabled={working} onClick={() => setCorrection(null)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" busy={working} busyLabel="Confirming…" onClick={() => confirm(provenance.derivedAt)}>
              That sounds like us
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={working}
              onClick={() => setCorrection({
                workspaceId,
                derivedAt: provenance.derivedAt,
                traits: {
                  tone: traits.tone,
                  formalityLevel: traits.formalityLevel,
                  emojiUsage: traits.emojiUsage,
                  responseLength: traits.responseLength
                }
              })}
            >
              Correct her
            </Button>
            <Button variant="ghost" size="sm" disabled={working} onClick={derive}>
              Read it again
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  )
}
